/**
 * The audit's Report tab — the deliverable and its versions.
 *
 * One primary action, chosen by state: Generate report → Approve → Issue
 * report → Print or save as PDF. A version is a snapshot: its checklist,
 * returns position, exception summary, data scope and analyses are frozen when
 * it is generated, so regenerating makes a new version and never changes one
 * already made. Issuing locks the audit.
 *
 * The printable report renders the same sections through the same components,
 * so nothing reads differently on paper.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Box, Button, Step, StepLabel, Stepper } from '@mui/material';
import { useToast } from '../../provider/toast';
import {
	useAuditCoverageQuery,
	useAuditReportsQuery,
	useGenerateReportMutation,
	useSetReportStatusMutation,
} from '../../queries/reports';
import { DataScopeSummary } from '../shared/dataFilter';
import { useAuth } from '../../provider/auth';
import { PageHeader } from '../../provider/pageTitle';
import { Card, ConfirmDialog, Note, StatusBadge } from '../../shared/ui';
import { EmptyState, ErrorState, LoadingState } from '../shared/states';
import { DataTable, type Column } from '../shared/tables';
import { count, date, dateTime, moneyShort, percent } from '../../utils/format';
import { ClassificationSplit, Figure, Figures } from '../shared/badges';
import { useAuditContext } from '../audits/AuditWorkspace';
import { useTerm } from '../../provider/auth';
import {
	TONE_TO_BADGE,
	type AnalysisStatusTone,
} from '../analyses/analysisStatusPresentation';
import type {
	AuditCoverage,
	AuditDetail,
	ChecklistItem,
	Report,
	ReportAnalysis,
} from '../../schemas';

/**
 * `ReportAnalysis` is a version's own frozen snapshot (`code`, `status` as a
 * plain string, `findings`, `lastRunAt`, `outOfDate`) — a different, older
 * shape than the live `AuditAnalysis` overview `features/analyses` reads, and
 * out of this task's contract (nothing freezes a report version differently
 * here). Kept local rather than shared, so a future change to the live
 * overview's shape cannot silently break a report already issued — only the
 * tone-to-badge palette is shared with the live analysis status.
 */
function reportAnalysisStatusKey(
	row: Pick<ReportAnalysis, 'status' | 'kind' | 'findings'>,
): string {
	if (row.status !== 'COMPLETED') return row.status;
	if (row.kind === 'SUMMARY') return 'COMPLETED';
	/* Null = findings were never calculated for this result (switched off, or
     an older result) — never the same claim as "No findings". Versions
     frozen before this always carried a number for a completed test, so
     they read exactly as before. */
	if (row.findings === null) return 'NOT_CALCULATED';
	return row.findings > 0 ? 'FINDINGS' : 'NO_FINDINGS';
}

function ReportAnalysisStatusBadge({
	row,
}: {
	row: Pick<ReportAnalysis, 'status' | 'kind' | 'findings'>;
}) {
	const key = reportAnalysisStatusKey(row);
	const term = useTerm('analysisStatus', key);
	const label =
		key === 'FINDINGS'
			? `${count(row.findings)} finding${row.findings === 1 ? '' : 's'}`
			: key === 'NOT_CALCULATED'
				? 'Not calculated'
				: term.label;
	return (
		<Box
			component="span"
			className={`bdg ${TONE_TO_BADGE[(term.tone ?? 'neutral') as AnalysisStatusTone] ?? 'gray'}`}
			title={term.help || undefined}
		>
			{label}
		</Box>
	);
}

function reportResultFigure(
	row: Pick<ReportAnalysis, 'findings' | 'resultRows' | 'kind'>,
) {
	if (row.kind === 'TEST' && row.findings !== null)
		return count(row.findings);
	if (row.resultRows !== null) return `${count(row.resultRows)} rows`;
	return '—';
}

const TRACK = ['DRAFT', 'APPROVED', 'ISSUED'] as const;
const TRACK_LABEL: Record<(typeof TRACK)[number], string> = {
	DRAFT: 'Draft',
	APPROVED: 'Approved',
	ISSUED: 'Issued',
};

/** "01 Oct 2026, 14:05 by Asha Rao" — when, and who, never an id. */
function signed(at: string | null, by: string | null): string {
	if (!at) return '';
	return by ? `${dateTime(at)} by ${by}` : dateTime(at);
}

export default function ReportWorkspace() {
	const { audit, refresh, crumbs } = useAuditContext();
	const { permissions } = useAuth();
	const toast = useToast();
	const [approving, setApproving] = useState(false);
	const [issuing, setIssuing] = useState(false);
	const [printView, setPrintView] = useState(false);

	const reports = useAuditReportsQuery(audit.pkAuditId);
	const coverage = useAuditCoverageQuery(audit.pkAuditId);
	const generate = useGenerateReportMutation(audit.pkAuditId);
	const setStatus = useSetReportStatusMutation(audit.pkAuditId);

	const all = reports.data ?? [];
	/* Newest first from the API, so the current version is the head. */
	const latest = all[0] ?? null;
	const history = all.slice(1);

	/* The browser's "Save as PDF" names the file after the page title. */
	useEffect(() => {
		if (!printView || !latest) return;
		const before = document.title;
		document.title = `${audit.clientCode} - ${audit.name} - Report v${latest.version}`;
		return () => {
			document.title = before;
		};
	}, [printView, latest, audit.clientCode, audit.name]);

	if (reports.isPending) return <LoadingState label="Loading the report…" />;
	if (reports.error)
		return <ErrorState error={reports.error} onRetry={reports.refetch} />;

	const canSign = Boolean(permissions?.generateReports);
	const issued = latest?.status === 'ISSUED';
	const failingBlocking = (latest?.checklist ?? []).filter(
		(item) => item.blocking && !item.passed,
	);

	const runGenerate = () =>
		generate.mutate(undefined, {
			onSuccess: () => {
				void refresh();
				toast('ok', 'New report version generated.');
			},
			onError: (error) =>
				toast(
					'danger',
					error instanceof Error
						? error.message
						: 'Could not generate.',
				),
		});

	const changeStatus = (
		status: 'APPROVED' | 'ISSUED',
		overrideReason: string,
	) =>
		latest &&
		setStatus.mutate(
			{ reportId: latest.pkReportId, status, overrideReason },
			{
				onSuccess: () => {
					setApproving(false);
					setIssuing(false);
					void refresh();
					toast(
						'ok',
						status === 'ISSUED'
							? 'Report issued. This audit is now locked.'
							: 'Report approved.',
					);
				},
			},
		);

	if (printView && latest) {
		return (
			<div className="stack">
				<PageHeader
					breadcrumbs={[
						...crumbs,
						{ label: 'Report', onClick: () => setPrintView(false) },
					]}
					title={`Print view · version ${latest.version}`}
					actions={
						<Button
							variant="contained"
							onClick={() => window.print()}
						>
							Print or save as PDF
						</Button>
					}
				/>
				<ReportPaper
					audit={audit}
					report={latest}
					coverage={coverage.data ?? null}
				/>
			</div>
		);
	}

	/* One primary action, by state. */
	const primary: ReactNode = !latest ? (
		canSign && (
			<Button
				loading={generate.isPending}
				variant="contained"
				disabled={generate.isPending}
				onClick={runGenerate}
			>
				Generate report
			</Button>
		)
	) : latest.status === 'DRAFT' || latest.status === 'UNDER_REVIEW' ? (
		canSign && (
			<Button variant="contained" onClick={() => setApproving(true)}>
				Approve
			</Button>
		)
	) : latest.status === 'APPROVED' ? (
		canSign && (
			<Button variant="contained" onClick={() => setIssuing(true)}>
				Issue report
			</Button>
		)
	) : (
		<Button variant="contained" onClick={() => setPrintView(true)}>
			Print or save as PDF
		</Button>
	);

	return (
		<div className="stack">
			<PageHeader
				breadcrumbs={crumbs}
				title="Report"
				subtitle={latest ? `Version ${latest.version}` : undefined}
				hint={
					latest ? (
						<StatusBadge
							group="reportStatus"
							value={latest.status}
						/>
					) : undefined
				}
				actions={
					<Box display="flex" gap="var(--s2)" flexWrap="wrap">
						{latest && !issued && (
							<Button
								variant="outlined"
								onClick={() => setPrintView(true)}
							>
								Print or save as PDF
							</Button>
						)}
						{latest && !issued && canSign && (
							<Button
								loading={generate.isPending}
								variant="outlined"
								disabled={generate.isPending}
								onClick={runGenerate}
							>
								Generate new version
							</Button>
						)}
						{primary}
					</Box>
				}
			/>

			{!latest ? (
				<>
					<EmptyState
						title="No report yet"
						action={
							canSign ? (
								<Button
									variant="contained"
									disabled={generate.isPending}
									onClick={runGenerate}
								>
									Generate report
								</Button>
							) : undefined
						}
					>
						Generating records the checklist, the returns position,
						the exceptions and the analyses as they stand now, as
						version 1.
					</EmptyState>
					<CoverageCard
						coverage={coverage.data ?? null}
						scope={<DataScopeSummary scope={audit.dataScope} />}
					/>
				</>
			) : (
				<>
					{issued && (
						<Note tone="ok" row title="Issued">
							{signed(latest.issuedAt, latest.issuedByName)}. This
							audit is locked: its data scope, analyses and this
							version can no longer change.
						</Note>
					)}

					<Card title={`Version ${latest.version}`}>
						<div className="stack">
							<Stepper
								activeStep={Math.max(
									0,
									TRACK.indexOf(
										latest.status as (typeof TRACK)[number],
									),
								)}
								alternativeLabel={false}
							>
								{TRACK.map((key) => (
									<Step
										key={key}
										completed={
											TRACK.indexOf(key) <
												TRACK.indexOf(
													latest.status as (typeof TRACK)[number],
												) || latest.status === 'ISSUED'
										}
									>
										<StepLabel>
											{TRACK_LABEL[key]}
										</StepLabel>
									</Step>
								))}
							</Stepper>
							<p className="mut" style={{ margin: 0 }}>
								Generated{' '}
								{signed(
									latest.createdAt,
									latest.generatedByName,
								)}
								{latest.approvedAt && (
									<>
										{' '}
										· Approved{' '}
										{signed(
											latest.approvedAt,
											latest.approvedByName,
										)}
									</>
								)}
								{latest.issuedAt && (
									<>
										{' '}
										· Issued{' '}
										{signed(
											latest.issuedAt,
											latest.issuedByName,
										)}
									</>
								)}
							</p>
							{latest.overrideReason && (
								<Note
									tone="warn"
									title="Approved or issued with a checklist override"
								>
									{latest.overrideReason}
								</Note>
							)}
						</div>
					</Card>

					<Card title="Checklist">
						<ChecklistList items={latest.checklist} />
					</Card>

					<CoverageCard
						coverage={coverage.data ?? null}
						scope={<DataScopeSummary scope={latest.dataScope} />}
					/>

					<Card title="Returns reconciliation">
						<PositionFigures position={latest.position} />
					</Card>

					<Card title="Analyses included">
						<AnalysesIncluded analyses={latest.analyses} />
					</Card>

					<Card title="Exceptions">
						<ExceptionFigures summary={latest.exceptionSummary} />
					</Card>

					{history.length > 0 && (
						<Card title="Version history">
							<div className="card-bd flush">
								<VersionHistoryTable versions={history} />
							</div>
						</Card>
					)}

					<ConfirmDialog
						open={approving}
						onClose={() => setApproving(false)}
						onConfirm={(reason) => changeStatus('APPROVED', reason)}
						title={`Approve version ${latest.version}?`}
						question="Approval records that this version has been checked internally. It does not close the audit or send anything to the client."
						impact={
							failingBlocking.length > 0
								? `${failingBlocking.length} blocking check${failingBlocking.length === 1 ? ' has' : 's have'} not passed: ${failingBlocking.map((item) => item.label).join('; ')}. Approving anyway needs a written reason, which is kept with the report.`
								: undefined
						}
						confirmLabel="Approve"
						reasonRequired={failingBlocking.length > 0}
						reasonLabel="Reason for overriding the checklist"
						pending={setStatus.isPending}
						error={setStatus.error}
					/>
					<ConfirmDialog
						open={issuing}
						onClose={() => setIssuing(false)}
						onConfirm={(reason) => changeStatus('ISSUED', reason)}
						title={`Issue version ${latest.version} to ${audit.clientName}?`}
						question="Issuing hands this version over and locks the audit: its data scope and analyses can no longer change, and this version can never be edited."
						impact={
							failingBlocking.length > 0
								? `${failingBlocking.length} blocking check${failingBlocking.length === 1 ? ' has' : 's have'} not passed: ${failingBlocking.map((item) => item.label).join('; ')}. Issuing anyway needs a written reason, which is kept with the report.`
								: undefined
						}
						confirmLabel="Issue report"
						danger
						reasonRequired={failingBlocking.length > 0}
						reasonLabel="Reason for overriding the checklist"
						pending={setStatus.isPending}
						error={setStatus.error}
					/>
				</>
			)}
		</div>
	);
}

function CoverageCard({
	coverage,
	scope,
}: {
	coverage: AuditCoverage | null;
	scope: ReactNode;
}) {
	return (
		<Card title="What these figures cover">
			<div className="stack">
				{coverage && <CoverageBlock data={coverage} />}
				<div>
					<h4 style={{ margin: '0 0 var(--s2)' }}>Data scope</h4>
					{scope}
				</div>
			</div>
		</Card>
	);
}

function CoverageBlock({ data }: { data: AuditCoverage }) {
	return (
		<div className="stack">
			<Figures>
				<Figure label="Source files" value={count(data.files)} />
				<Figure
					label="Rows in scope"
					value={count(data.rows)}
					meta={
						data.unknown > 0
							? `${count(data.unknown)} unclassified`
							: undefined
					}
					tone={data.unknown > 0 ? 'warn' : undefined}
				/>
				{data.filesExcluded > 0 && (
					<Figure
						label="Files excluded"
						value={count(data.filesExcluded)}
						tone="warn"
					/>
				)}
				{data.filesAwaiting > 0 && (
					<Figure
						label="Replacement requested"
						value={count(data.filesAwaiting)}
						tone="warn"
					/>
				)}
			</Figures>
			<ClassificationSplit
				rows={{
					SALES: data.sales,
					RETURNS: data.returns,
					ADJUSTMENT: data.adjustments,
					UNKNOWN: data.unknown,
					EXCLUDED: 0,
				}}
				total={data.rows}
				compact
			/>
		</div>
	);
}

function ChecklistList({ items }: { items: ChecklistItem[] }) {
	if (items.length === 0) return <p className="mut">Nothing was checked.</p>;
	/* Failing blocking items first — they are what stops the report. */
	const ordered = [...items].sort(
		(a, b) =>
			Number(a.passed) - Number(b.passed) ||
			Number(b.blocking) - Number(a.blocking),
	);
	return (
		<div className="checklist">
			{ordered.map((item) => (
				<div key={item.key}>
					<StatusBadge
						group="checkStatus"
						value={item.passed ? 'PASSED' : 'FAILED'}
					/>
					<div>
						<span className="l">{item.label}</span>
						<span className="d">{item.detail}</span>
					</div>
					<span>
						{item.blocking && (
							<span className="small mut">Blocks issuing</span>
						)}
					</span>
				</div>
			))}
		</div>
	);
}

/** Each figure independently null — "not run" never renders as zero. */
function PositionFigures({ position }: { position: Report['position'] }) {
	const notRun = 'Not run';
	return (
		<Figures>
			<Figure
				label="Matched returns"
				value={
					position.matched_returns === null
						? '—'
						: count(position.matched_returns)
				}
				meta={position.matched_returns === null ? notRun : undefined}
			/>
			<Figure
				label="Unmatched returns"
				value={
					position.flagged_returns === null
						? '—'
						: count(position.flagged_returns)
				}
				tone={(position.flagged_returns ?? 0) > 0 ? 'warn' : undefined}
				meta={position.flagged_returns === null ? notRun : undefined}
			/>
			<Figure
				label="Unmatched value"
				value={
					position.flagged_value === null ? (
						'—'
					) : (
						<span
							title={`₹${position.flagged_value.toLocaleString('en-IN')}`}
						>
							{moneyShort(position.flagged_value)}
						</span>
					)
				}
				tone={(position.flagged_value ?? 0) > 0 ? 'warn' : undefined}
				meta={position.flagged_value === null ? notRun : undefined}
			/>
			<Figure
				label="Match rate"
				value={
					position.match_rate_pct === null
						? '—'
						: percent(position.match_rate_pct)
				}
				meta={position.match_rate_pct === null ? notRun : undefined}
			/>
		</Figures>
	);
}

function ExceptionFigures({
	summary,
}: {
	summary: Report['exceptionSummary'];
}) {
	return (
		<Figures>
			<Figure label="Total" value={count(summary.total)} />
			<Figure label="Open" value={count(summary.open)} />
			<Figure
				label="Blocking, open"
				value={count(summary.blocking)}
				tone={summary.blocking > 0 ? 'bad' : 'ok'}
			/>
			<Figure label="Accepted" value={count(summary.accepted)} />
			<Figure label="Corrected" value={count(summary.corrected)} />
			<Figure label="Closed" value={count(summary.closed)} />
			<Figure
				label="Value at stake (open)"
				value={
					<span
						title={`₹${summary.openValue.toLocaleString('en-IN')}`}
					>
						{moneyShort(summary.openValue)}
					</span>
				}
			/>
		</Figures>
	);
}

function AnalysesIncluded({ analyses }: { analyses: ReportAnalysis[] | null }) {
	if (!analyses) {
		return (
			<p className="mut">
				Not recorded for this version (generated before analyses were
				recorded).
			</p>
		);
	}
	const columns: Array<Column<ReportAnalysis>> = [
		{
			key: 'ref',
			label: 'Ref.',
			width: 72,
			sortValue: (row) => row.code,
			render: (row) => row.code,
		},
		{
			key: 'name',
			label: 'Analysis',
			width: 320,
			sortValue: (row) => row.name,
			render: (row) => row.name,
		},
		{
			key: 'status',
			label: 'Status',
			width: 150,
			render: (row) => (
				<ReportAnalysisStatusBadge
					row={{
						status: row.status as
							| 'NOT_RUN'
							| 'WAITING'
							| 'RUNNING'
							| 'COMPLETED'
							| 'FAILED',
						kind: row.kind,
						findings: row.findings,
					}}
				/>
			),
		},
		{
			key: 'figure',
			label: 'Findings / rows',
			num: true,
			width: 140,
			render: (row) => reportResultFigure(row),
		},
		{
			key: 'run',
			label: 'Last run',
			width: 180,
			render: (row) => (
				<span>
					{row.lastRunAt ? dateTime(row.lastRunAt) : '—'}
					{row.outOfDate && (
						<span className="text-warn"> · out of date</span>
					)}
				</span>
			),
		},
	];
	return (
		<div className="card-bd flush">
			<DataTable
				rows={analyses}
				columns={columns}
				rowKey={(row) => row.slug}
				caption="Analyses included"
				maxHeight="short"
				initialSort={{ key: 'ref', direction: 'asc' }}
			/>
		</div>
	);
}

function VersionHistoryTable({ versions }: { versions: Report[] }) {
	const columns: Array<Column<Report>> = [
		{
			key: 'version',
			label: 'Version',
			width: 90,
			render: (r) => `Version ${r.version}`,
		},
		{
			key: 'status',
			label: 'Status',
			width: 130,
			render: (r) => (
				<StatusBadge group="reportStatus" value={r.status} />
			),
		},
		{
			key: 'generated',
			label: 'Generated',
			width: 200,
			render: (r) => signed(r.createdAt, r.generatedByName),
		},
		{
			key: 'approved',
			label: 'Approved',
			width: 200,
			render: (r) => signed(r.approvedAt, r.approvedByName) || '—',
		},
		{
			key: 'issued',
			label: 'Issued',
			width: 200,
			render: (r) => signed(r.issuedAt, r.issuedByName) || '—',
		},
		{
			key: 'override',
			label: 'Override reason',
			width: 260,
			render: (r) =>
				r.overrideReason ? (
					<span className="small">{r.overrideReason}</span>
				) : (
					'—'
				),
		},
	];
	return (
		<DataTable
			rows={versions}
			columns={columns}
			rowKey={(r) => r.pkReportId}
			caption="Earlier report versions"
			maxHeight="short"
		/>
	);
}

/**
 * The printable report: a document, not a screenshot. The same sections through
 * the same components, inside `.paper` with its print stylesheet.
 */
function ReportPaper({
	audit,
	report,
	coverage,
}: {
	audit: AuditDetail;
	report: Report;
	coverage: AuditCoverage | null;
}) {
	return (
		<Card>
			<div className="paper">
				<h1>{audit.name}</h1>
				<div className="rule-hd">
					{audit.clientName} ({audit.clientCode}) ·{' '}
					{date(audit.periodStart)} – {date(audit.periodEnd)}
				</div>
				<p className="small">
					Report version {report.version} ·{' '}
					<StatusBadge group="reportStatus" value={report.status} /> ·
					Generated {signed(report.createdAt, report.generatedByName)}
					{report.approvedAt && (
						<>
							{' '}
							· Approved{' '}
							{signed(report.approvedAt, report.approvedByName)}
						</>
					)}
					{report.issuedAt && (
						<>
							{' '}
							· Issued{' '}
							{signed(report.issuedAt, report.issuedByName)}
						</>
					)}
				</p>
				{report.overrideReason && (
					<Note
						tone="warn"
						title="Approved or issued with a checklist override"
					>
						{report.overrideReason}
					</Note>
				)}

				<h2>1 · What these figures cover</h2>
				{coverage ? (
					<CoverageBlock data={coverage} />
				) : (
					<p className="small mut">Coverage could not be loaded.</p>
				)}
				<DataScopeSummary scope={report.dataScope} />

				<h2>2 · Checklist</h2>
				<ChecklistList items={report.checklist} />

				<h2>3 · Returns reconciliation</h2>
				<PositionFigures position={report.position} />

				<h2>4 · Analyses included</h2>
				<AnalysesIncluded analyses={report.analyses} />

				<h2>5 · Exceptions</h2>
				<ExceptionFigures summary={report.exceptionSummary} />
			</div>
		</Card>
	);
}
